import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { RefreshCw, Loader2, AlertCircle } from 'lucide-react';
import { apiClient } from '@/services/api/apiClient';
import { toast } from 'sonner';

/**
 * Stripe Connect Refresh Page
 *
 * This page handles expired onboarding links and generates new ones.
 * Users land here if they navigate away from Stripe and the link expires (1 hour).
 */
export default function StripeConnectRefresh() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const restAccountId = searchParams.get('rest_account_id');

  const [status, setStatus] = useState<'loading' | 'error'>('loading');
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    // Auto-attempt to refresh link on page load
    handleRefreshLink();
  }, []);

  const handleRefreshLink = async () => {
    try {
      setRefreshing(true);
      console.log('🔄 [STRIPE_REFRESH] Requesting new onboarding link...');

      // Call backend to generate a new onboarding link
      const response = await apiClient.post('/stripe-connect/refresh-link');
      const data = response.data;

      console.log('✅ [STRIPE_REFRESH] New link generated:', data);

      if (data.url) {
        toast.success('New onboarding link generated!');

        // Redirect to the new Stripe onboarding URL
        window.location.href = data.url;
      } else {
        throw new Error('No onboarding URL in response');
      }
    } catch (error: any) {
      console.error('❌ [STRIPE_REFRESH] Error refreshing link:', error);
      setStatus('error');
      toast.error(error.response?.data?.detail || 'Failed to refresh onboarding link');
    } finally {
      setRefreshing(false);
    }
  };

  const handleReturnToSetup = () => {
    navigate('/virtual-host/setup');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Refresh Stripe Onboarding
          </h1>
          <p className="text-gray-600">
            Let's get you back on track
          </p>
        </div>

        {/* Content */}
        <div className="flex flex-col items-center justify-center min-h-[300px]">
          {status === 'loading' && (
            <div className="text-center">
              <Loader2 className="w-16 h-16 text-purple-600 animate-spin mx-auto mb-4" />
              <p className="text-lg text-gray-700 font-semibold">
                Generating new onboarding link...
              </p>
              <p className="text-sm text-gray-500 mt-2">
                Please wait a moment
              </p>
            </div>
          )}

          {status === 'error' && (
            <div className="text-center">
              <AlertCircle className="w-20 h-20 text-orange-500 mx-auto mb-6" />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Link Refresh Failed
              </h2>
              <p className="text-gray-600 mb-6">
                We couldn't generate a new onboarding link. This might happen if your account setup is incomplete.
              </p>

              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6 text-left">
                <h3 className="font-semibold text-orange-900 mb-2">What you can do:</h3>
                <ul className="space-y-2 text-sm text-orange-800">
                  <li>• Return to the setup wizard and try again</li>
                  <li>• Make sure you have a Stripe account created first</li>
                  <li>• Contact support if the problem persists</li>
                </ul>
              </div>

              <div className="flex gap-4 justify-center">
                <button
                  onClick={handleRefreshLink}
                  disabled={refreshing}
                  className="px-6 py-3 bg-orange-600 hover:bg-orange-700 text-white rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
                  {refreshing ? 'Retrying...' : 'Try Again'}
                </button>

                <button
                  onClick={handleReturnToSetup}
                  className="px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-semibold transition-colors"
                >
                  Return to Setup
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Info Box */}
        <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h3 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            Why did this happen?
          </h3>
          <p className="text-sm text-blue-800">
            Stripe onboarding links expire after 1 hour for security reasons.
            If you navigated away or took a break, you'll need a fresh link to continue.
            Don't worry - your progress is saved!
          </p>
        </div>

        {/* Footer */}
        <div className="mt-8 pt-6 border-t border-gray-200 text-center">
          <p className="text-sm text-gray-500">
            Need help? Contact support at{' '}
            <a href="mailto:support@dineai.ai" className="text-purple-600 hover:underline">
              support@dineai.ai
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
